import React from 'react';
import { Send, Settings, Clock, Loader2 } from 'lucide-react';

const MessageComposeModal = ({
  isOpen,
  onClose,
  // Recipient
  composeRecipient,
  setComposeRecipient,
  // Contact search
  showContactSuggestions,
  setShowContactSuggestions,
  filteredContacts,
  setFilteredContacts,
  loadingContacts,
  iCloudContacts,
  contactsList,
  fetchICloudContacts,
  // Message content
  composeMessage,
  setComposeMessage,
  // Send from phone selection
  sendFromPhone,
  setSendFromPhone,
  userPhoneNumbers,
  onOpenPhoneSettings,
  // Scheduling
  onOpenScheduleModal,
  // Send handler
  onSend,
  sendingMessage
}) => {
  if (!isOpen) return null;

  const handleRecipientChange = async (e) => {
    const searchValue = e.target.value;
    setComposeRecipient(searchValue);
    
    if (searchValue.trim().length >= 1) {
      if (iCloudContacts.length === 0 && !loadingContacts) {
        await fetchICloudContacts();
        return;
      }
      
      setShowContactSuggestions(true);
      
      const currentContacts = iCloudContacts.length > 0 ? iCloudContacts : contactsList;
      const filtered = currentContacts.filter(contact => {
        const searchLower = searchValue.toLowerCase();
        const nameMatch = contact.name?.toLowerCase().includes(searchLower);
        const phoneMatch = contact.phone?.includes(searchValue);
        return nameMatch || phoneMatch;
      });
      
      setFilteredContacts(filtered);
    } else {
      setShowContactSuggestions(false);
      setFilteredContacts([]);
    }
  };

  const handleRecipientFocus = () => {
    if (composeRecipient.trim() && contactsList.length > 0) {
      setShowContactSuggestions(true);
      const filtered = contactsList.filter(contact => {
        const searchLower = composeRecipient.toLowerCase();
        const nameMatch = contact.name?.toLowerCase().includes(searchLower);
        const phoneMatch = contact.phone?.includes(composeRecipient);
        return nameMatch || phoneMatch;
      });
      setFilteredContacts(filtered);
    }
  };

  const handleRecipientBlur = () => {
    setTimeout(() => setShowContactSuggestions(false), 200);
  };

  const selectContact = (contact) => {
    setComposeRecipient(contact.phone);
    setShowContactSuggestions(false);
    setFilteredContacts([]);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-gray-900 border border-gray-400/20 rounded-2xl w-[600px] flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="p-6 border-b border-gray-400/20">
          <h2 className="text-2xl font-bold text-white">New Message</h2>
          <p className="text-gray-400 text-sm mt-1">Compose a new text message</p>
        </div>

        {/* Compose Form */}
        <div className="p-6 space-y-4">
          {/* To Field with Contact Search */}
          <div className="relative">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              To: {loadingContacts && <span className="text-purple-400 text-xs ml-2">(Loading contacts...)</span>}
            </label>
            <input
              type="text"
              value={composeRecipient}
              onChange={handleRecipientChange}
              onFocus={handleRecipientFocus}
              onBlur={handleRecipientBlur}
              placeholder="Enter contact name or phone number (e.g., +1234567890)"
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400"
              data-testid="compose-recipient-input"
            />
            
            {/* Contact Suggestions Dropdown */}
            {showContactSuggestions && filteredContacts.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {filteredContacts.map((contact, idx) => (
                  <button
                    key={idx}
                    onClick={() => selectContact(contact)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-700 transition-colors border-b border-gray-400/10 last:border-b-0"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold">
                        {contact.name?.split(' ').map(n => n[0]).join('').slice(0, 2) || '??'}
                      </div>
                      <div>
                        <div className="text-white font-medium">{contact.name}</div>
                        <div className="text-xs text-gray-400">{contact.phone}</div>
                        {contact.source === 'icloud' && (
                          <div className="text-xs text-purple-400">iCloud Contact</div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
            
            {/* Loading state */}
            {showContactSuggestions && loadingContacts && (
              <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl p-4 text-center">
                <div className="text-gray-400">Loading iCloud contacts...</div>
              </div>
            )}
            
            {/* No contacts found */}
            {showContactSuggestions && !loadingContacts && filteredContacts.length === 0 && composeRecipient.trim() && (
              <div className="absolute z-50 w-full mt-1 bg-gray-800 border border-gray-400/20 rounded-lg shadow-xl p-4 text-center">
                <div className="text-gray-400">No contacts found. Enter a phone number directly.</div>
              </div>
            )}
          </div>

          {/* Send From Phone Selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-300">Send From:</label>
              <button 
                onClick={onOpenPhoneSettings}
                className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
                data-testid="open-phone-settings-btn"
              >
                <Settings size={12} />
                Configure Numbers
              </button>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSendFromPhone('personal')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  sendFromPhone === 'personal'
                    ? 'bg-green-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="send-from-personal-btn"
              >
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                Personal
                {userPhoneNumbers.personal && (
                  <span className="text-xs opacity-70">{userPhoneNumbers.personal.slice(-4)}</span>
                )}
              </button>
              <button
                onClick={() => setSendFromPhone('business')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  sendFromPhone === 'business'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="send-from-business-btn"
              >
                <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                Business
                {userPhoneNumbers.business && (
                  <span className="text-xs opacity-70">{userPhoneNumbers.business.slice(-4)}</span>
                )}
              </button>
              <button
                onClick={() => setSendFromPhone('twilio')}
                className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                  sendFromPhone === 'twilio'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
                data-testid="send-from-twilio-btn"
              >
                <div className="w-2 h-2 rounded-full bg-purple-400"></div>
                Twilio
              </button>
            </div>
          </div>

          {/* Message Field */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Message:</label>
            <textarea
              value={composeMessage}
              onChange={(e) => setComposeMessage(e.target.value)}
              rows="5"
              placeholder="Type your message..."
              className="w-full px-4 py-2 bg-gray-800 border border-gray-400/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-400 resize-none"
              data-testid="compose-message-textarea"
            />
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{composeMessage.length} / 160 characters</span>
              <span>{Math.ceil(composeMessage.length / 160) || 1} SMS segment(s)</span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="p-6 border-t border-gray-400/20 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors text-white font-medium"
          >
            Cancel
          </button>
          <button
            onClick={onOpenScheduleModal}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-white font-medium flex items-center gap-2"
            data-testid="schedule-message-btn"
          >
            <Clock size={18} />
            Schedule
          </button>
          <button
            onClick={onSend}
            disabled={sendingMessage || !composeRecipient.trim() || !composeMessage.trim()}
            className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors text-white font-medium flex items-center justify-center gap-2"
            data-testid="send-message-btn"
          >
            {sendingMessage ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={18} />
                Send Message
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MessageComposeModal;
